// src/pages/TermsPage.tsx

import { FC } from 'react';
import styles from '../css/TermsPage.module.css';
import { motion, Variants } from 'framer-motion';
import { ShieldAlert, Scale, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

const TermsPage: FC = () => {
    const lastUpdated = "November 20, 2025";

    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.termsPage}>
            
            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Legal Framework
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Operational <br/>
                                <span className={styles.titleHighlight}>Protocols & Terms.</span>
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                Creating a transparent foundation for our digital interactions. 
                                Please review these terms carefully before using our platforms.
                            </motion.p>

                            <motion.div variants={itemVariants} className={styles.metaInfo}>
                                <span className={styles.dateLabel}>Last Updated: {lastUpdated}</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. LEGAL CONTENT */}
            <AnimatedSection className={styles.contentSection}>
                <div className={styles.container}>
                    <div className={styles.documentLayout}>
                        
                        {/* Sidebar Navigation (Visual decoration for this page) */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarSticky}>
                                <div className={styles.iconBlock}>
                                    <Scale size={24} strokeWidth={1.5} />
                                </div>
                                <h3 className={styles.sidebarTitle}>Table of Contents</h3>
                                <ul className={styles.tocList}>
                                    <li>1. Acceptance of Terms</li>
                                    <li>2. Intellectual Property</li>
                                    <li>3. User Obligations</li>
                                    <li>4. Liability & Disclaimers</li>
                                    <li>5. Governing Law</li>
                                </ul>
                                <Link to="/" className={styles.backLink}>
                                    <ArrowLeft size={14} /> Return Home
                                </Link>
                            </div>
                        </aside>

                        {/* Main Text Content */}
                        <div className={styles.legalTextWrapper}>
                            
                            <div className={styles.legalSection}>
                                <h2>1. Acceptance of Terms</h2>
                                <p>
                                    By accessing or using the website, services, or platforms operated by OTA TECH Inc. ("we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not access or use our services.
                                </p>
                                <p>
                                    These terms apply to all visitors, users, and others who access or use the Service ("Users").
                                </p>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>2. Intellectual Property Rights</h2>
                                <p>
                                    The Service and its original content, features, and functionality (including but not limited to the OTA ERP architecture, proprietary algorithms, and "ANNA AI" logic) are and will remain the exclusive property of OTA TECH Inc. and its licensors.
                                </p>
                                <p>
                                    Our intellectual property is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of OTA TECH Inc.
                                </p>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>3. User Obligations & Conduct</h2>
                                <p>
                                    You agree not to use the Service for any unlawful purpose or in any way that interrupts, damages, impairs, or renders the Service less efficient. You specifically agree not to:
                                </p>
                                <ul>
                                    <li>Attempt to reverse engineer any portion of the software.</li>
                                    <li>Use data mining, robots, or similar data gathering extraction tools.</li>
                                    <li>Attempt to gain unauthorized access to our private API endpoints.</li>
                                </ul>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>4. Limitation of Liability</h2>
                                <div className={styles.alertBox}>
                                    <ShieldAlert size={20} className={styles.alertIcon} />
                                    <p>
                                        To the maximum extent permitted by law, OTA TECH Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
                                    </p>
                                </div>
                                <p>
                                    Our services are provided on an "AS IS" and "AS AVAILABLE" basis. We do not warrant that the service will be uninterrupted, secure, or free from errors.
                                </p>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>5. Governing Law</h2>
                                <p>
                                    These Terms shall be governed and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.
                                </p>
                                <p>
                                    Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                                </p>
                            </div>

                            <div className={styles.footerNote}>
                                <p>For legal inquiries, please contact: <strong>legal@otatech.org</strong></p>
                            </div>

                        </div>
                    </div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default TermsPage;